import { Box, Grid, Button, TextField, InputAdornment, SvgIcon, Autocomplete, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Typography, useTheme, IconButton, Tooltip } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { SearchSm } from '../../../../assets';
import CustomPagination from '../../../../components/forms/Pagination';
import { UserContext } from '../../../../components/shared/useUser';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import useLogError from '../../../../hooks/useLogError';
import { useContext, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ProductRow from './ProductRow';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { starbucksFranchises } from '../../../../enums/FranchiseNames';
import { OrderCategoriesTranslationMap, OrdersCategoriesParentsMap, isOrderCategory, isOrderCategoryParent } from '../../../../enums/OrderCategories';
import { PredictedDemandModel } from '../../../../app/services/api/podClient';
import { ItemsFilterOptions, client, Item, Category, OrderDetails, ContainerFillResult } from '../../../../app/services/api/orderManagementClient';
import { useNavigate } from 'react-router-dom';
import { useActiveOrder } from '../../../../contexts/ActiveOrderContext';

const Products = ({ orderIdString }: { orderIdString: string | undefined }) => {

  const orderId = orderIdString ? parseInt(orderIdString, 10) : 0;
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const { t } = useTranslation('products');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { setActiveOrder, setContainerData: setActiveContainerData } = useActiveOrder();

  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageReset = useRef(false);
  const productRowRefs = useRef<{ matchPredictedQuantities: () => void }[]>([]);

  const [itemSearch, setItemSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<'asc' | 'desc'>('asc');
  const [category, setCategory] = useState<number>(-1); const [categories, setCategories] = useState<Category[]>([]);
  const [localActiveOrder, setLocalActiveOrder] = useState<OrderDetails>();
  const { setShippingDetails } = useActiveOrder();
  const [supplier, setSupplier] = useState<string>('');
  const [showPredictedQuantity] = useState<boolean>(true);
  const [predictedDemands] = useState<PredictedDemandModel[]>([]); 
  const [orderDetails, setOrderDetails] = useState<OrderDetails>();

  const pageSize = 10;
  const isStarBucksRetail = starbucksFranchises.includes(selectedStore?.franchiseName ?? '');
  const showMessageWhenNoCost = isStarBucksRetail && localActiveOrder?.status != 'Order Confirmed'; const [items, setItems] = useState<Item[]>([]);
  const [orderTypeId, setOrderTypeId] = useState<number>(0);

  const scrollOnTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const getFilterOptions = (storeNumber: string, itemSearchString: string, categoryId: number, currentOrderType: number, currentSupplier: string, pageNumber: number, currentPageSize: number, sort: string, direction: string): ItemsFilterOptions => {
    return {
      storeNumber: storeNumber,
      item: itemSearchString,
      categoryId: categoryId,
      orderType: currentOrderType,
      supplier: currentSupplier,
      pageNumber: pageNumber,
      pageSize: currentPageSize,
      sort: sort,
      direction: direction,
    };
  };

  const sortItemsByActiveOrder = (allItems: Item[], selectedOrder: OrderDetails) => {
    const sortedItems: Item[] = [];
    const existingItems = allItems.filter(item =>
      selectedOrder.items.some(i => i.sku === item.sku),
    );
    const possibleItems = allItems.filter((element) => !existingItems.includes(element));
    sortedItems.push(...existingItems, ...possibleItems);
    setItems(sortedItems);
  };

  const fetchProducts = async (filters: ItemsFilterOptions, activeOrder?: OrderDetails) => {
    try {
      const itemsResponse = await client.getItems(filters);
      setItems(itemsResponse.data.items);
      setTotalPages(itemsResponse.data.totalPages);
      setTotalCount(itemsResponse.data.totalCount);
      if (page !== itemsResponse.data.pageNumber) {
        pageReset.current = true;
        setPage(itemsResponse.data.pageNumber);
      }
      if (activeOrder) {
        sortItemsByActiveOrder(itemsResponse.data.items, activeOrder);
      }
    } catch (error) {
      logError(error);
      console.error(t('fetchingErrorMessage') + ' ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const activateShippingMethod = async (
    selectedStore?: string,
    order?: OrderDetails ,
  ) => {
  
    try {

      const result = await client.getShippingDetails(
        selectedStore ?? '',
        order?.id?.toString() ?? '',
      );
      if (result.success && result.data) {
        const data = result.data;
        const chargeValue = data.deliveryCharge || 0;
        setShippingDetails({
          deliveryMethod: data.deliveryMethod,
          deliveryCharge: chargeValue.toFixed(2),
        });
        await client.setShippingDetails(
          selectedStore ?? '',
          order?.id?.toString() ?? '',
          {
            deliveryCharge: chargeValue ?? 0,
            deliveryMethod:  data.deliveryMethod,
            deliveryMethodId: data.deliveryMethodId ?? '',
            orderStatus: order?.statusId.toString() ?? '',
            deliveryOptions: data.deliveryOptions,
            country: data.country,
            location: data.location,
            containerType: data.containerType ?? 0,
            palletType: data.palletType ?? 0,
          },
        );
      }
    }
    catch (error) {
      logError(error);
    }
  };

  const fetchCategories = async (selectedOrderType: number, currentSupplier: string) => {
    try {
      const categoriesResponse = await client.getCategories(selectedStore?.storeNumber, selectedOrderType, currentSupplier);
      setCategories(categoriesResponse.data);
    } catch (error) {
      setCategories([]);
    }
  };

  const loadData = async (storeNumber: string | undefined, itemSearchString: string, categoryId: number, currentOrderTypeId: number, currentSupplier: string, pageNumber: number, currentPageSize: number, sort: string, direction: string) => {
    if (storeNumber) {
      setLoading(true);
      await fetchProducts(getFilterOptions(storeNumber, itemSearchString, categoryId, currentOrderTypeId, currentSupplier, pageNumber, currentPageSize, sort, direction));
      await fetchCategories(currentOrderTypeId, currentSupplier);
      scrollOnTop();
      setLoading(false);
    }
  };

  const fetchOrderDetails = async () => {

    if (!selectedStore?.storeNumber || !orderId) {
      return;
    }

    try {

      const result = await client.getOrder(selectedStore.storeNumber, orderId);
      setOrderDetails(result.data);
      const currentOrderTypeId = result.data.orderTypeId;
      const currentSupplier = result.data.supplierNumber;
      setOrderTypeId(currentOrderTypeId);
      setLocalActiveOrder(result.data);
      setActiveOrder(result.data);
      setSupplier(currentSupplier);

      if (selectedStore?.storeNumber && result.data.id) {
        try {
          const containerResult = await client.getStoreContainerData(
            selectedStore.storeNumber,
            result.data.id.toString(),
          );
          setActiveContainerData(containerResult.data);
          await activateShippingMethod(selectedStore.storeNumber, result.data);

        } catch (err) {
          logError(err);
          console.error(t('fetchingErrorMessage') + ' ' + err);
        }
      }

      await fetchProducts(
        getFilterOptions(selectedStore.storeNumber, '', -1, currentOrderTypeId, currentSupplier, 0, pageSize, 'Name', 'asc'),
        result.data,
      );
      await fetchCategories(currentOrderTypeId, currentSupplier);

    } catch (error) {
      console.error(t('fetchingErrorMessage') + ' ' + (error instanceof Error ? error.message : String(error)));
      throw error;
    }
  };

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    const performInitialLoad = async () => {
      if (!selectedStore?.storeNumber) {
        return;
      }
      setLoading(true);
      try {
        if (orderId) {
          await fetchOrderDetails();
        } else {
          setLocalActiveOrder(undefined);
          setActiveOrder(null);
          setActiveContainerData(null);
          await loadData(selectedStore.storeNumber, '', -1, orderTypeId, supplier, 0, pageSize, 'Name', 'asc');
        }
      } catch (ex) {
        console.error('Error during initial load:', ex);
        console.error(t('fetchingErrorMessage') + ' ' + (ex instanceof Error ? ex.message : String(ex)));
      } finally {
        setLoading(false);
      }
    };

    performInitialLoad();
  }, [selectedStore, orderId]);

  useEffect(() => {
    const fetchItemsOnPagination = async () => {
      if (!loading && orderTypeId !== -1 && !pageReset.current && selectedStore?.storeNumber) {
        setLoading(true);
        await fetchProducts({
          item: itemSearch,
          orderType: orderTypeId,
          storeNumber: selectedStore?.storeNumber,
          categoryId: category,
          supplier: supplier,
          pageNumber: page,
          pageSize: pageSize,
          sort: 'Name',
          direction: sortBy,
        });
        setLoading(false);
      }
      pageReset.current = false;
      scrollOnTop();
    };
    if (orderTypeId !== 0 || !orderId) {
      fetchItemsOnPagination().catch((ex) => {
        console.error(ex);
        console.error(t('fetchingErrorMessage') + ' ' + (ex instanceof Error ? ex.message : String(ex)));
        setLoading(false);
      });
    }
  }, [page]);

  const handleSearch = async () => {
    await loadData(selectedStore?.storeNumber, itemSearch, category, orderTypeId, supplier, page, pageSize, 'Name', sortBy);
  };

  const clearFilters = async () => {
    setItemSearch('');
    setCategory(-1);
    setSortBy('asc');
    setPage(0);
    await loadData(selectedStore?.storeNumber, '', -1, orderTypeId, supplier, 0, pageSize, 'Name', 'asc');
  };

  const onAddItemConfirmed = async () => {
    if (!selectedStore?.storeNumber || !orderDetails?.id) return; const updatedOrder = await client.getOrder(selectedStore.storeNumber, orderDetails.id);
    setOrderDetails(updatedOrder.data);
    setLocalActiveOrder(updatedOrder.data);
    setActiveOrder(updatedOrder.data);

    if (selectedStore?.storeNumber && updatedOrder.data.id) {
      try {
        const containerResult = await client.getStoreContainerData(
          selectedStore.storeNumber,
          updatedOrder.data.id.toString(),
        );
        setActiveContainerData(containerResult.data);
      } catch (err) {
        logError(err);
        console.error(t('fetchingErrorMessage') + ' ' + err);
      }
    }

    sortItemsByActiveOrder(items, updatedOrder.data);
  };

  const getBasketQuantity = (itemId: number) => {
    return localActiveOrder?.items?.find(item => item.itemId === itemId)?.quantity ?? 0;
  };

  const autocompleteOptions = [{ id: -1, name: 'All', parentName: '' }, ...(Array.isArray(categories) ? categories : [])];

  const getOptionLabel = (option: { parentName?: string; name: string }): string => {
    const parentName = option.parentName;
    const categoryName = option.name;

    const translatedParent = parentName && isOrderCategoryParent(parentName)
      ? t(OrdersCategoriesParentsMap[parentName as keyof typeof OrdersCategoriesParentsMap])
      : parentName;

    const translatedCategory = isOrderCategory(categoryName)
      ? t(OrderCategoriesTranslationMap[categoryName as keyof typeof OrderCategoriesTranslationMap])
      : categoryName;

    if (parentName) {
      return translatedParent
        ? `${translatedParent} > ${translatedCategory}`
        : `${parentName} > ${translatedCategory}`;
    } else {
      return translatedCategory;
    }
  };

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', flex: 1, alignContent: 'center', my: '24px', py: '5px' }}>
      <Box
        mb={6}
        px="24px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex"
          alignItems="center">
          <Typography variant="textLG">{t('orderableItems')}</Typography>
          <Typography
            variant="textXS"
            sx={{
              border: `1px solid ${theme.palette.primary[400]}`,
              py: 2,
              px: 3,
              mx: 3,
              borderRadius: '6px',
              color: theme.palette.common.white,
              background: theme.palette.primary[500],
            }}
          >
            {totalCount} {t('items')}
          </Typography>
        </Box>

        <Tooltip title="Go to basket"
          arrow>
          <IconButton
            onClick={() => navigate(`/order/${orderId}`)}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              p: 1.5,
              borderRadius: '8px',
            }}
          >
            <ShoppingCartIcon />
          </IconButton>
        </Tooltip>

      </Box>
      <Box mt={4}
        sx={{
          boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
          border: `1px solid ${theme.palette.custom.gray[200]}`,
        }}>
        <Grid item
          container
          spacing={6}
          p={3}
        >
          <Grid item
            lg={3}
            xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('searchBy')}
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon>
                      <SearchSm />
                    </SvgIcon>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item
            lg={3}
            xs={12}>
            <Autocomplete
              size="small"
              options={autocompleteOptions}
              getOptionLabel={getOptionLabel}
              value={categories.find(cat => cat.id === category) ?? autocompleteOptions[0]}
              onChange={(event, newValue) => {
                setCategory(newValue ? newValue.id : -1);
              }}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('category')}
                  placeholder="Choose..." />
              )}
            />
          </Grid>

          <Grid item
            lg={3}
            xs={12}>
            <Button
              fullWidth
              variant="secondary"
              disabled={loading}
              onClick={clearFilters}
              size="lg"
            >
              {t('clearAll')}
            </Button>
          </Grid>

          <Grid item
            lg={3}
            xs={12}>
            <Button
              fullWidth
              variant="primary"
              disabled={loading}
              onClick={handleSearch}
              size="lg"
              startIcon={<SearchSm />}
            >
              {t('search')}
            </Button>
          </Grid>

        </Grid>
      </Box>

      <Grid item
        xs={12}
        py={2}>
        {loading ? (
          <LoadingWheel />
        ) :
          <>
            <TableContainer>
              <Table>
                <TableHead
                  sx={{
                    pt: '10px',
                  }}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 'bold' }}
                      align="center">
                      {t('itemDetails')}
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold' }}
                      align="left">
                      {t('quantity')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <ProductRow key={item.id}
                      product={item}
                      selectedOrderId={orderId}
                      orderItemId={orderDetails?.items.find(i => i.sku === item.sku)?.id}
                      basketQuantity={getBasketQuantity(item.id)}
                      predictedItem={predictedDemands.find(x => x.itemNumber === item.sku)}
                      showPredictedQuantity={showPredictedQuantity}
                      onAddItemConfirmed={onAddItemConfirmed}
                      currencyCode={currencyCode}
                      showMessageWhenNoCost={showMessageWhenNoCost}
                      orderType={orderTypeId}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      ref={(ref: any) => {
                        productRowRefs.current[item.id] = ref!;
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer><CustomPagination
              page={page}
              setPage={setPage}
              maxPages={totalPages}
              breakpointForChangeDisplay={120000}
            ></CustomPagination>
          </>
        }
      </Grid>
    </Paper >
  );
};

export default Products;